import { flatten, unflatten } from '../../lib/helpers';
import {
  DELIMITER,
  INTERPOLATION_KEY,
  SIGNIN_INPUT_JSON_PROPERTIES,
  METHOD_SELECTION_JSON_PROPERTIES,
} from '../../constants/returns/uiBuilder';

const {
  INPUT_BASE,
  INPUT_LABEL,
  INPUT_GIFT_LABEL,
} = SIGNIN_INPUT_JSON_PROPERTIES;

const {
  STEP_1: { INITIATED_RETURNS_HEADER },
  STEP_2: {
    RETURN_METHODS_BASE,
    TITLE,
    FEATURE_1,
    FEATURE_2,
    FEATURE_3,
    STORE_LINK_TEXT,
    STORE_LINK,
    DESCRIPTION,
    DISCLAIMER,
    PRICE_LABEL,
  },
  STEP_3: { SUBHEADER, REVIEW_LABEL },
  STEP_4: {
    EMAIL_CONFIRMATION,
    EMAIL_SECONDARY_CONFIRMATION,
    INSTRUCTIONS_TITLE,
    INSTRUCTION_0_HEADER,
    INSTRUCTION_0_DESCRIPTION,
    INSTRUCTION_0_BUTTON,
    INSTRUCTION_1_HEADER,
    INSTRUCTION_1_DESCRIPTION,
    INSTRUCTION_2_HEADER,
    INSTRUCTION_2_DESCRIPTION,
  },
} = METHOD_SELECTION_JSON_PROPERTIES;

export const deleteFromLanguage = (languageKeys, language) => {
  const trimmedLanguage = { ...language };
  languageKeys.forEach(property => {
    delete trimmedLanguage[property];
  });
  return trimmedLanguage;
};

export const getSupplementaryLanguage = (languageKeys, languageTemplate) => {
  const supplementaryLanguage = {};
  languageKeys.forEach(property => {
    supplementaryLanguage[property] = languageTemplate[property];
  });
  return supplementaryLanguage;
};

export const generateMethodKey = ({ return_method: returnMethod, carrier }) =>
  carrier ? `${returnMethod}_${carrier}` : returnMethod;

export const getAvailableMethods = (
  returnsSettings,
  returnsSettingsTemplate,
) => {
  const {
    returns: { return_methods: returnMethods = [] } = {},
  } = returnsSettings;

  const {
    returns: { return_methods: returnMethodsTemplate = [] } = {},
  } = returnsSettingsTemplate;

  const availableMethods = returnMethodsTemplate.filter(methodTemplate => {
    const methodKeyTemplate = generateMethodKey(methodTemplate);
    const enabledTemplate = returnMethods.find(
      method =>
        method.enabled && methodKeyTemplate === generateMethodKey(method),
    );
    return !enabledTemplate;
  });

  return availableMethods;
};

export const constructReturnMethodMappings = methodKey => ({
  initiatedReturnsHeader: `${INITIATED_RETURNS_HEADER}${methodKey}`,
  title: `${RETURN_METHODS_BASE}${methodKey}${TITLE}`,
  firstBenefit: `${RETURN_METHODS_BASE}${methodKey}${FEATURE_1}`,
  secondBenefit: `${RETURN_METHODS_BASE}${methodKey}${FEATURE_2}`,
  thirdBenefit: `${RETURN_METHODS_BASE}${methodKey}${FEATURE_3}`,
  linkText: `${RETURN_METHODS_BASE}${methodKey}${STORE_LINK_TEXT}`,
  linkURL: `${RETURN_METHODS_BASE}${methodKey}${STORE_LINK}`,
  description: `${RETURN_METHODS_BASE}${methodKey}${DESCRIPTION}`,
  disclaimer: `${RETURN_METHODS_BASE}${methodKey}${DISCLAIMER}`,
  priceLabel: `${RETURN_METHODS_BASE}${methodKey}${PRICE_LABEL}`,
  subheader: `${SUBHEADER}${methodKey}`,
  reviewLabel: `${REVIEW_LABEL}${methodKey}`,
  emailConfirmation: `${EMAIL_CONFIRMATION}${methodKey}`,
  emailSecondaryConfirmation: `${EMAIL_SECONDARY_CONFIRMATION}${methodKey}`,
  instructionsTitle: `${INSTRUCTIONS_TITLE}${methodKey}`,
  instruction0Header: `${INSTRUCTION_0_HEADER}${methodKey}`,
  instruction0Description: `${INSTRUCTION_0_DESCRIPTION}${methodKey}`,
  instruction0Button: `${INSTRUCTION_0_BUTTON}${methodKey}`,
  instruction1Header: `${INSTRUCTION_1_HEADER}${methodKey}`,
  instruction1Description: `${INSTRUCTION_1_DESCRIPTION}${methodKey}`,
  instruction2Header: `${INSTRUCTION_2_HEADER}${methodKey}`,
  instruction2Description: `${INSTRUCTION_2_DESCRIPTION}${methodKey}`,
});

export const constructSignInInputMappings = inputName => ({
  inputLabel: `${INPUT_BASE}${inputName}${INPUT_LABEL}`,
  giftInputLabel: `${INPUT_BASE}${inputName}${INPUT_GIFT_LABEL}`,
});

export const getLanguageKeysFromMethodKey = methodKey => {
  const langMappings = constructReturnMethodMappings(methodKey);
  const langKeys = Object.values(langMappings);
  return langKeys;
};

export const SMART_CREATE_SOURCES = {
  mail: 'mail_ups',
  printerless_mail: 'printerless_mail_ups',
  pickup: 'pickup_ups',
};

const SOURCE_TOKENS = /https?:\/\/[^\s"'<>]+|\bUPS/g;
const SUB_VARIANT = /\.(mi|smart_post)(\.|\[|$)/;
const RETURN_OPTIONS_BASE = 'returns.return_options.';
const STEP_3_KEY = sourceMethod => `${REVIEW_LABEL}${sourceMethod}`;
const CARD_TITLE_KEY = (cardId, methodKey) =>
  `${RETURN_OPTIONS_BASE}${cardId}.${methodKey}${TITLE}`;
const READINESS_KEY = {
  mail: CARD_TITLE_KEY,
  printerless_mail: CARD_TITLE_KEY,
  pickup: (cardId, methodKey) => STEP_3_KEY(methodKey),
};
const HAS_URL = /https?:\/\//;

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const longerSources = sourceMethod =>
  Object.values(SMART_CREATE_SOURCES).filter(
    other => other !== sourceMethod && other.includes(sourceMethod),
  );

export const isEnglishLocale = locale => /^en_/.test(locale || '');

export const smartCreateDisplayName = label =>
  (label || '')
    .replace(/\s+(printerless|mobile return code|carrier pickup|pickup)$/i, '')
    .trim();

export const smartCreateSourceLanguageKeys = (
  languageTemplate,
  sourceMethod,
) => {
  const longer = longerSources(sourceMethod);
  return Object.keys(languageTemplate || {}).filter(key => {
    const index = key.indexOf(sourceMethod);
    return (
      index >= 0 &&
      !key.startsWith(RETURN_METHODS_BASE) &&
      !longer.some(other => key.includes(other)) &&
      !SUB_VARIANT.test(key.slice(index + sourceMethod.length))
    );
  });
};

export const hasSmartCreateStrings = (languageTemplate, cardId) => {
  const sourceMethod = SMART_CREATE_SOURCES[cardId];
  return Boolean(
    sourceMethod &&
      (languageTemplate || {})[READINESS_KEY[cardId](cardId, sourceMethod)],
  );
};

export const smartCreateNeedsStoreLink = (languageTemplate, cardId) => {
  const sourceMethod = SMART_CREATE_SOURCES[cardId];
  return smartCreateSourceLanguageKeys(languageTemplate, sourceMethod).some(
    key => HAS_URL.test(String((languageTemplate || {})[key])),
  );
};

export const matchesEnUsCopy = (languageTemplate, enUsTemplate, cardId) => {
  const sourceMethod = SMART_CREATE_SOURCES[cardId];
  if (!sourceMethod) return false;
  const enUsValue = (enUsTemplate || {})[STEP_3_KEY(sourceMethod)];
  return Boolean(
    enUsValue &&
      (languageTemplate || {})[STEP_3_KEY(sourceMethod)] === enUsValue,
  );
};

const LOCALE_STRINGS_REQUIRED = ['printerless_mail', 'pickup'];

export const blocksOnMissingStrings = ({
  cardId,
  locale,
  languageTemplate,
  enUsTemplate,
}) =>
  LOCALE_STRINGS_REQUIRED.includes(cardId) &&
  !hasSmartCreateStrings(languageTemplate, cardId) &&
  Boolean(enUsTemplate) &&
  !isEnglishLocale(locale) &&
  !matchesEnUsCopy(languageTemplate, enUsTemplate, cardId);

export const smartCreateSourceConfig = (settingsTemplate, cardId) => {
  const { returns: { returnOptions: { methods = {} } = {} } = {} } =
    settingsTemplate || {};
  const sourceMethod = SMART_CREATE_SOURCES[cardId];
  if (!sourceMethod) return undefined;
  if (methods[sourceMethod]) return methods[sourceMethod];
  const sibling = Object.keys(methods)
    .sort()
    .find(key => key.startsWith(`${cardId}_`));
  return sibling ? methods[sibling] : undefined;
};

// Items must never carry a `name` property — hum's Checkbox uses
// `item.name || index` as the input name the select handler parseInts.
export const buildMethodsSelectData = ({
  cardId,
  options = [],
  configuredMethods = {},
  selectedMethods = [],
  supportedReturnMethods = {},
}) => {
  const alphaSort = (a, b) => (a.label || '').localeCompare(b.label || '');
  const smartCreate = Boolean(SMART_CREATE_SOURCES[cardId]);
  const prefix = `${cardId}_`;

  const cardOptions = smartCreate
    ? [
        ...options,
        ...Object.keys(configuredMethods).filter(
          key => key.startsWith(prefix) && !options.includes(key),
        ),
      ]
    : options;

  const configured = cardOptions
    .map(option => ({
      data: option,
      label: (supportedReturnMethods[option] || {}).label || option,
      selected: selectedMethods.includes(option),
    }))
    .sort(alphaSort);

  if (!smartCreate) return configured;

  const candidate = new RegExp(`^${escapeRegExp(prefix)}[^.]+$`);
  const needsSetup = Object.keys(supportedReturnMethods)
    .filter(key => candidate.test(key) && !cardOptions.includes(key))
    .map(key => ({
      data: key,
      label: (supportedReturnMethods[key] || {}).label || key,
      selected: false,
      needsSetup: true,
    }))
    .sort(alphaSort);

  return [...configured, ...needsSetup];
};

export const buildSmartCreateMethod = ({
  cardId,
  methodKey,
  displayName,
  storeLink,
  sourceConfig,
  sourceLanguageTemplate,
}) => {
  const sourceMethod = SMART_CREATE_SOURCES[cardId];
  const methodConfig = JSON.parse(JSON.stringify(sourceConfig));
  const carrier = methodKey.slice(cardId.length + 1);
  (methodConfig.store_locator_query_params || []).forEach(param => {
    if (param.key === 'carriers') {
      // eslint-disable-next-line no-param-reassign
      param.value = [carrier];
    }
  });

  const methodLanguage = {};
  smartCreateSourceLanguageKeys(sourceLanguageTemplate, sourceMethod).forEach(
    sourceKey => {
      const key = sourceKey.split(sourceMethod).join(methodKey);
      const sourceValue = sourceLanguageTemplate[sourceKey];
      methodLanguage[key] =
        typeof sourceValue === 'string'
          ? sourceValue.replace(SOURCE_TOKENS, token =>
              token.startsWith('http') ? storeLink : displayName,
            )
          : '';
    },
  );

  return { methodConfig, methodLanguage };
};

export const buildDynamicLanguage = ({
  dynamicLanguageKeys = [],
  methodKeys = [],
  source = {},
  existing = {},
}) =>
  dynamicLanguageKeys.reduce((acc, { renderKey, dynamicKey }) => {
    if (existing[renderKey]) return acc;
    const value =
      methodKeys.length > 1
        ? source[renderKey]
        : source[
            dynamicKey.replace(
              INTERPOLATION_KEY,
              methodKeys[methodKeys.length - 1],
            )
          ];
    return value === undefined ? acc : { ...acc, [renderKey]: value };
  }, {});

export const getRefundTextLanguageKeyFromRefundType = refundType =>
  `returns.step_3.refunds.payments.${refundType}.text`;

export const getUnfilteredIdxFromFilteredIdx = (
  returnMethods,
  filteredIndex,
) => {
  const enabledReturnMethods = returnMethods.filter(({ enabled }) => enabled);
  const methodKey = generateMethodKey(enabledReturnMethods[filteredIndex]);
  const unfilteredIndex = returnMethods.findIndex(
    method => generateMethodKey(method) === methodKey,
  );
  return unfilteredIndex > -1 ? unfilteredIndex : filteredIndex;
};

export const flattenSettings = settings =>
  flatten(settings, { delimiter: DELIMITER });

export const unflattenSettings = settings =>
  unflatten(settings, { delimiter: DELIMITER });
